/**
 * Gaps & Risks Page
 * Identify compliance gaps and mitigate risks
 * Uses reusable components from /components/obligations
 */
import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Search, 
  Filter, 
  Shield,
  TrendingUp,
  Clock,
  CheckCircle2,
  XCircle,
  Users,
  FileX,
  Loader2,
  RefreshCw,
  ExternalLink
} from 'lucide-react';

import RiskStatCard from '../../components/obligations/RiskStatCard';
import CriticalObligationCard from '../../components/obligations/CriticalObligationCard';
import EvidenceGapCard from '../../components/obligations/EvidenceGapCard';
import CapacityRiskCard from '../../components/obligations/CapacityRiskCard';
import ObligationDetailDrawer from '../../components/obligations/ObligationDetailDrawer';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { DataQualityCheck, GapDetection } from '../journeys/JourneyObligations';
import { apiFetch } from '../../services/api_service';

export default function GapsRisksPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gapsData, setGapsData] = useState({ gaps: [], summary: {} });
  const [activeTab, setActiveTab] = useState('critical');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [selectedObligation, setSelectedObligation] = useState(null);

  const fetchGapsAndRisks = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch('/obligations/gaps');
      if (data?._error) throw new Error('Failed to fetch gaps and risks');
      setGapsData(data);
    } catch (err) {
      console.error('Error fetching gaps:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGapsAndRisks();
  }, []);

  // Process gaps data
  const gaps = gapsData.gaps || [];
  const summary = gapsData.summary || {};
  
  // Categorize gaps
  const criticalGaps = gaps.filter(g => g.gap_type === 'overdue' || g.severity === 'critical');
  const evidenceGaps = gaps.filter(g => g.gap_type === 'missing_evidence' || g.evidence_gaps_count > 0);
  const blockedGaps = gaps.filter(g => g.gap_type === 'blocked');
  
  // Calculate capacity risks (mock for now)
  const capacityRisks = [
    {
      userId: 'user_sarah',
      userName: 'Sarah Chen',
      userRole: 'Data Owner',
      riskLevel: 'high',
      activeObligations: 5,
      overdueCount: 1,
      avgProgress: 45,
      workload_percentage: 92,
      recommendation: 'Consider redistributing 2 obligations to balance workload.'
    }
  ].filter(() => summary.overdue_count > 0);

  // Calculate compliance score
  const complianceScore = summary.total_gaps 
    ? Math.max(0, Math.round((1 - summary.total_gaps / 20) * 100))
    : 100;

  // Filter gaps based on search and severity
  const filterGaps = (gapList) => {
    return gapList.filter(gap => {
      const matchesSearch = !searchQuery || 
        (gap.obligation_title || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (gap.framework_code || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSeverity = filterSeverity === 'all' || 
        (gap.severity || 'medium') === filterSeverity ||
        (gap.gap_type === 'overdue' && filterSeverity === 'critical');
      return matchesSearch && matchesSeverity;
    });
  };

  const filteredCritical = filterGaps(criticalGaps);
  const filteredEvidence = filterGaps(evidenceGaps);
  const filteredBlocked = filterGaps(blockedGaps);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="gaps-loading">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
        <span className="ml-2 text-[#6B6B6B]">Analyzing gaps and risks...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6" data-testid="gaps-risks-page">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 text-center" data-testid="gaps-error">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-3" />
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchGapsAndRisks}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg"
          >
            Try Again
          </button>
        </div>
        
        {/* Live User Journeys - shown even on error */}
        <JourneySection title="Live Journeys">
          <JourneyPanel title="Data Quality Check" ucId="UC-11"><DataQualityCheck /></JourneyPanel>
          <JourneyPanel title="Gap Detection" ucId="UC-25"><GapDetection /></JourneyPanel>
        </JourneySection>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gaps-risks-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-2xl font-bold text-white">Gaps & Risks</h1>
            {criticalGaps.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-bold bg-red-500/20 text-red-400 rounded animate-pulse">
                {criticalGaps.length} CRITICAL
              </span>
            )}
          </div>
          <p className="text-sm text-[#6B6B6B]">
            Identify and address compliance gaps and data quality risks
          </p>
        </div>
        <button 
          onClick={fetchGapsAndRisks}
          className="p-2 rounded-lg bg-[#1F1F1F] hover:bg-[#2F2F2F] text-[#6B6B6B] hover:text-white transition-colors"
          title="Refresh"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Stats Banner */}
      <div className="grid grid-cols-4 gap-4">
        <RiskStatCard
          icon={XCircle}
          label="Critical Gaps"
          value={criticalGaps.length}
          description="Overdue or high-risk"
          bgColor="bg-red-500/20"
        />
        <RiskStatCard
          icon={FileX}
          label="Evidence Gaps"
          value={evidenceGaps.length}
          description="Missing evidence"
          bgColor="bg-amber-500/20"
        />
        <RiskStatCard
          icon={Shield}
          label="Total Gaps"
          value={summary.total_gaps || 0}
          description="All compliance gaps"
          bgColor="bg-purple-500/20"
        />
        <RiskStatCard
          icon={TrendingUp}
          label="Compliance Score"
          value={`${complianceScore}%`}
          description="Based on gap analysis"
          bgColor="bg-emerald-500/20"
        />
      </div>

      {/* Summary Alerts */}
      {(summary.overdue_count > 0 || summary.blocked_count > 0) && (
        <div className="grid grid-cols-2 gap-4">
          {summary.overdue_count > 0 && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={16} className="text-red-400" />
                <span className="text-sm font-medium text-red-400">Overdue Items</span>
              </div>
              <p className="text-xs text-[#A0A0A0]">
                {summary.overdue_count} obligation(s) are past their due date and require immediate attention.
              </p>
            </div>
          )}
          {summary.blocked_count > 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
              <div className="flex items-center gap-2 mb-2">
                <XCircle size={16} className="text-amber-400" />
                <span className="text-sm font-medium text-amber-400">Blocked Items</span>
              </div>
              <p className="text-xs text-[#A0A0A0]">
                {summary.blocked_count} obligation(s) are blocked and cannot proceed until dependencies are resolved.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center gap-2 border-b border-[#1F1F1F] pb-2">
        {[
          { id: 'critical', label: 'Critical Issues', icon: AlertTriangle, count: criticalGaps.length, color: 'red' },
          { id: 'evidence', label: 'Evidence Gaps', icon: FileX, count: evidenceGaps.length, color: 'amber' },
          { id: 'coverage', label: 'Coverage Gaps', icon: Shield, count: blockedGaps.length, color: 'purple' },
          { id: 'capacity', label: 'Capacity Risks', icon: Users, count: capacityRisks.length, color: 'blue' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                : 'text-[#6B6B6B] hover:text-white hover:bg-[#1F1F1F]'
            }`}
          >
            <tab.icon size={14} />
            {tab.label}
            {tab.count > 0 && (
              <span className={`px-1.5 py-0.5 text-[10px] rounded ${
                tab.color === 'red' ? 'bg-red-500/20 text-red-400' :
                tab.color === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                tab.color === 'purple' ? 'bg-purple-500/20 text-purple-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Filters */}
      {(activeTab === 'critical' || activeTab === 'evidence') && (
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B6B6B]" />
            <input
              type="text"
              placeholder="Search gaps..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-[#111111] border border-[#1F1F1F] rounded-lg text-sm text-white placeholder:text-[#6B6B6B] focus:border-emerald-500/50 focus:outline-none"
              data-testid="gaps-search-input"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-[#6B6B6B]" />
            {['all', 'critical', 'high', 'medium', 'low'].map(severity => (
              <button
                key={severity}
                onClick={() => setFilterSeverity(severity)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors ${
                  filterSeverity === severity 
                    ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30'
                    : 'bg-[#1F1F1F] text-[#6B6B6B] hover:text-white'
                }`}
              >
                {severity}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div className="space-y-4">
        {/* Critical Issues Tab */}
        {activeTab === 'critical' && (
          <>
            {filteredCritical.length === 0 ? (
              <EmptyState
                icon={Shield}
                title="No critical issues detected"
                description="All obligations are on track. Great work!"
              />
            ) : (
              filteredCritical.map((gap, idx) => (
                <CriticalObligationCard
                  key={gap.obligation_id || idx}
                  obligation={gap}
                  onSelect={() => setSelectedObligation(gap.obligation_id)}
                />
              ))
            )}
          </>
        )}

        {/* Evidence Gaps Tab */}
        {activeTab === 'evidence' && (
          <>
            {filteredEvidence.length === 0 ? (
              <EmptyState
                icon={FileX}
                title="No evidence gaps"
                description="All required evidence has been collected"
              />
            ) : (
              filteredEvidence.map((gap, idx) => (
                <EvidenceGapCard
                  key={gap.obligation_id || idx}
                  gap={gap}
                  onSelect={() => setSelectedObligation(gap.obligation_id)}
                />
              ))
            )}
          </>
        )}

        {/* Coverage Gaps Tab */}
        {activeTab === 'coverage' && (
          <>
            {filteredBlocked.length === 0 ? (
              <EmptyState
                icon={Shield}
                title="Full framework coverage"
                description="All framework requirements have been mapped to obligations"
              />
            ) : (
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                <p className="text-sm text-purple-300">
                  {filteredBlocked.length} obligation(s) are blocked and may create coverage gaps.
                </p>
                <div className="mt-4 space-y-2">
                  {filteredBlocked.map((gap, idx) => (
                    <div 
                      key={gap.obligation_id || idx}
                      className="p-3 bg-[#111111] border border-[#1F1F1F] rounded-lg cursor-pointer hover:border-purple-500/30"
                      onClick={() => setSelectedObligation(gap.obligation_id)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">{gap.obligation_title}</span>
                        <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded">Blocked</span>
                      </div>
                      {gap.blocked_reason && (
                        <p className="text-xs text-[#6B6B6B] mt-1">{gap.blocked_reason}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Capacity Risks Tab */}
        {activeTab === 'capacity' && (
          <>
            {capacityRisks.length === 0 ? (
              <EmptyState
                icon={Users}
                title="Balanced workload"
                description="No capacity issues detected across the team"
              />
            ) : (
              capacityRisks.map((risk, idx) => (
                <CapacityRiskCard key={risk.userId || idx} risk={risk} />
              ))
            )}
          </>
        )}
      </div>

      {/* Obligation Detail Drawer */}
      <ObligationDetailDrawer
        obligationId={selectedObligation}
        open={!!selectedObligation}
        onClose={() => setSelectedObligation(null)}
      />

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="AI Data Quality Check" ucId="UC-11"><DataQualityCheck /></JourneyPanel>
        <JourneyPanel title="Compliance Gap Detection" ucId="UC-25"><GapDetection /></JourneyPanel>
      </JourneySection>
    </div>
  );
}

// Empty State Component
function EmptyState({ icon: Icon, title, description }) {
  return (
    <div className="text-center py-12 bg-[#111111] border border-emerald-500/30 rounded-xl">
      <Icon size={48} className="mx-auto text-emerald-500/50 mb-4" />
      <h3 className="text-lg font-medium text-white mb-2">{title}</h3>
      <p className="text-sm text-[#6B6B6B]">{description}</p>
    </div>
  );
}
